import { useEffect, useState, useMemo } from "react";
import { ReMixChart } from "../../../../../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../../../../../models/chart_model";
import loader from "../../../../../../../components/recharts/loader.gif";
import { StateOutageSectorResponse } from "../../../../../../../Rest_api/RestApi";

interface ChartDataItem {
  name: string; // Sector name
  outage: number;
  monitored_cap: number;
  [key: string]: any;
}

export interface DailyVolumeAnalysisProps {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  selectedTargetDate: any[];
  data: StateOutageSectorResponse | null;
  selectedDate?: string;
}

export default function DailyVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  selectedTargetDate,
  data,
  selectedDate,
}: DailyVolumeAnalysisProps) {
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);

  useEffect(() => {
    if (!data || !selectedDate) {
      setChartData([]);
      return;
    }

    // Determine which range contains the selected date
    let selectedData = data.range_1?.[selectedDate] || data.range_2?.[selectedDate];

    // If not found in direct lookup, try matching date string keys
    if (!selectedData) {
      // Only needed if keys format differs, but usually they match
    }

    if (!selectedData) {
      setChartData([]);
      return;
    }

    // Transform to chart data and sort by outage descending for consistent order
    const newData: ChartDataItem[] = Object.entries(selectedData).map(([sector, values]) => ({
      name: sector,
      outage: values.outage || 0,
      monitored_cap: values.monitored_cap || 0,
    })).sort((a, b) => b.outage - a.outage);

    setChartData(newData);
  }, [data, selectedDate]);

  const legends = useMemo(() => {
    return [
      {
        dataKey: "outage",
        name: "Capacity Under Outage",
        type: ChartType.Bar,
        unit: "MW",
        stroke: "#FF5858", // Red
        color: "#FF5858",
        legendColor: "#FF5858",
      },
      {
        dataKey: "monitored_cap",
        name: "Total Capacity",
        type: ChartType.Bar,
        unit: "MW",
        stroke: "rgba(43, 127, 255, 1)", // Blue
        color: "#2B7FFF",
        legendColor: "#2B7FFF",
      }
    ];
  }, []);

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[300px]">
          <p className="text-gray-500">
            {!data
              ? "Please Select 2 Date Ranges"
              : "No data available for the selected date"}
          </p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          legends={legends}
          xDataKey="name"
          isTimeSlot={false}
          yAxisLabel="Total Volume (MW)"
          showBrush={false}
          showToolbox={false}
          top={30}
          bottom={30}
          barCategoryGap={20}
          xAxisInterval={0}
          colorByPoint={false}
          fontSize={10}
          isSmallLegend={true}
          xaxisHeight={50}
          brushHeight={15}
        />
      )}
    </>
  );
}

